import { AddButton, ReactTableContextProvider, ReactTableContext, PageSpinner } from '@td-client-frontend/common';
import React, { ChangeEvent, useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import { Nullable } from '../../@types/common';
import { ClientRepsUser, UserMutationData } from '../../@types/Users';
import { Button } from '../../components/Button';

import Modal from '../../components/Modal/NewModal';
import { SearchTextInput } from '../../components/SearchTextInput';
import WithPermission from '../../components/WithPermission';
import { useAuth } from '../../context/AuthContext';
import { useClientRepsList } from '../../hooks/useClientRepsList';
import { NewUserModal } from '../Users/CreateUsersModal/NewUserModalContent';
import { ManageUserModal } from '../Users/Manage/ManageUserModal';
import { UserDetailsModal } from '../Users/UserDetailsModal';
import ManageUsersTable from './ManageUsersTable';

function ManageUsers() {
  const { showNoAccessModal, checkPerm } = useAuth();
  const [search, setSearch] = useState<string | undefined>('');
  const [debouncedSearch] = useDebounce<string | undefined>(search, 2000);
  const [usersMutation, setUsersMutation] = useState<UserMutationData[]>([]);
  const [selectedUser, setSelectedUser] = useState<Nullable<number>>(null);
  const [checkedUsers, setCheckedUsers] = useState<string[]>([]);

  // Modal States
  const [showNewUserModal, setShowNewUserModal] = useState<boolean>(false);
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState<boolean>(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState<boolean>(false);

  const {
    state: { sortBy }
  } = useContext(ReactTableContext);
  const {
    data: clientRepsUsers,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    updateUsers
  } = useClientRepsList({
    search: debouncedSearch,
    sorting: sortBy[0]?.desc ? 'DESC' : 'ASC'
  });
  const users = clientRepsUsers?.pages.flatMap((page) => page.clientRepsUsers) || [];

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onUserCheck = (checked: boolean, id: string) => {
    return !checked
      ? setCheckedUsers((prev) => [...prev, id])
      : setCheckedUsers((prev) => prev.filter((roleId) => roleId !== id));
  };

  const onMainCheck = useCallback(() => {
    const isAllRolesSelected: boolean = !!(users.length && users.length === checkedUsers.length);
    return !isAllRolesSelected ? setCheckedUsers(users.map((user) => user.id)) : setCheckedUsers([]);
  }, [checkedUsers.length, users]);

  const toggleStatus = () => {
    setUsersMutation(
      users.reduce((acc: UserMutationData[], user: ClientRepsUser) => {
        if (checkedUsers.includes(user.id)) acc.push({ id: user.id, active: !user.active });
        return acc;
      }, [])
    );
    setShowToggleStatusModal(true);
  };

  const onToggleStatus = async () => {
    const { status } = await updateUsers(usersMutation);
    if (status === 200) {
      setCheckedUsers([]);
      toggleStatusModal();
    }
  };

  const onManage = useCallback(
    (id: string) => {
      const user: ClientRepsUser | undefined = users?.find((user: ClientRepsUser) => user.id === id);
      const readOnly: boolean = !checkPerm('UserWrite');
      // seems like GET /api/users/client-reps/{id} only supports active users
      if (user && user.active) {
        setSelectedUser(Number(user.id));
        return readOnly ? setShowUserDetailsModal(true) : setShowEditUserModal(true);
      }
      return toast.error('User must be active', { position: 'top-center' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPerm, users, showNoAccessModal]
  );

  const toggleNewUserModal = () => setShowNewUserModal(!showNewUserModal);
  const toggleStatusModal = () => setShowToggleStatusModal(!showToggleStatusModal);
  const toggleEditUserModal = () => setShowEditUserModal(!showEditUserModal);
  const toggleUserDetailsModal = () => setShowUserDetailsModal(!showUserDetailsModal);

  return (
    <div className="flex flex-col items-center justify-between w-full px-6 py-4 pt-10 mx-auto bg-white sm:items-baseline">
      <Modal
        isOpen={showNewUserModal}
        onClose={toggleNewUserModal}
        contentLabel="Add user Modal"
        title="Add user Modal"
        header="Add User"
        size="large"
      >
        <NewUserModal onRequestClose={toggleNewUserModal} />
      </Modal>

      <Modal
        isOpen={showToggleStatusModal}
        onClose={toggleStatusModal}
        contentLabel="Toggle User Status Modal"
        title="Toggle User Status Modal"
        header="Toggle User Status"
        size="prompt"
        actions={[
          { title: 'Cancel', kind: 'secondary' },
          {
            title: 'Yes, Toggle',
            kind: 'primary',
            autoClose: false,
            onClick: onToggleStatus
          }
        ]}
      >
        <section className="px-6 pb-6 font-medium">
          <p>Are you sure you want to toggle {usersMutation.length} user(s) status?</p>
        </section>
      </Modal>

      <Modal
        isOpen={showEditUserModal}
        onClose={toggleEditUserModal}
        contentLabel="Manage user Modal"
        title="Manage user Modal"
        header="Manage user"
        size="large"
      >
        {/* @ts-ignore */}
        <ManageUserModal userId={selectedUser} hideModal={toggleEditUserModal} />
      </Modal>

      <Modal
        isOpen={showUserDetailsModal}
        onClose={toggleUserDetailsModal}
        contentLabel="user details"
        title="user details"
        header="View user details"
        size="large"
        actions={[{ title: 'Cancel', kind: 'secondary' }]}
      >
        <UserDetailsModal userId={selectedUser} />
      </Modal>

      <div className="flex flex-col w-full flex-1 overflow-hidden">
        <div className="flex items-center justify-between w-full px-6 my-8">
          <h1 className="text-4xl font-effra">Manage users</h1>
          <WithPermission requiredPerms={['UserCreate']}>
            <AddButton onClick={toggleNewUserModal} label="Add user" />
          </WithPermission>
        </div>
        <div className="w-1/4 px-1">
          <SearchTextInput
            value={search}
            onChange={handleSearchChange}
            onClear={() => setSearch('')}
            placeholder="Search..."
          />
        </div>
        <div className="flex-none flex-grow min-h-full overflow-x-none">
          <ManageUsersTable
            usersData={users}
            onUserManage={onManage}
            isFetching={isFetching && !isFetchingNextPage}
            checkedUsers={checkedUsers}
            onMainCheck={onMainCheck}
            onUserCheck={onUserCheck}
          />
        </div>
        {hasNextPage && (
          <div className="w-full flex items-center justify-center py-4 text-lg border-b border-gray-20">
            {isFetchingNextPage ? (
              <PageSpinner />
            ) : (
              <button
                onClick={() => fetchNextPage()}
                className="text-primary-500 font-effra text-lg"
                data-testid="load-more-profiles"
              >
                Load more
              </button>
            )}
          </div>
        )}
        <div className="flex flex-col pb-16 sm:flex-row sm:flex-wrap justify-end bottom-0 right-0 px-6 py-5 w-full bg-white z-50 transition-all duration-150">
          <WithPermission requiredPerms={['UserWrite']}>
            <Button name="toggle-btn" title="Toggle Status" onClick={toggleStatus} disabled={!checkedUsers.length} />
          </WithPermission>
        </div>
      </div>
    </div>
  );
}

const ManageUsersWithTableProvider = (props: any) => (
  <ReactTableContextProvider>
    <ManageUsers {...props} />
  </ReactTableContextProvider>
);

export default ManageUsersWithTableProvider;
